import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../../data/mockData";
import { Clock, ArrowRight } from "lucide-react";

const BlogCard = ({ post, onClick }) => {
  return (
    <article
      className="group cursor-pointer"
      onClick={() => onClick(post)}
    >
      <div className="rounded-xl overflow-hidden mb-4 aspect-[3/2]">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex items-center gap-3 mb-3">
        <span className="px-3 py-1 bg-[#E8F5E9] text-[#3A7D44] text-xs font-semibold rounded-full">
          {post.category}
        </span>
        <span className="text-xs text-[#5A6B5A]">{post.date}</span>
      </div>
      <h3 className="text-lg font-bold text-[#1B2A1B] mb-2 group-hover:text-[#3A7D44] transition-colors leading-snug">
        {post.title}
      </h3>
      <p className="text-sm text-[#5A6B5A] leading-relaxed mb-3 line-clamp-3">
        {post.excerpt}
      </p>
      <span className="inline-flex items-center gap-1 text-sm font-medium text-[#3A7D44] group-hover:gap-2 transition-all">
        Read article <ArrowRight size={14} />
      </span>
    </article>
  );
};

const BlogFeaturedPost = ({ post, onClick }) => {
  return (
    <div
      className="group mb-12 cursor-pointer"
      onClick={() => onClick(post)}
    >
      <div className="grid md:grid-cols-2 gap-8 items-center bg-[#F9F7F4] rounded-2xl overflow-hidden">
        <div className="aspect-[4/3] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-[#E8F5E9] text-[#3A7D44] text-xs font-semibold rounded-full">
              {post.category}
            </span>
            <span className="text-sm text-[#5A6B5A]">{post.date}</span>
          </div>
          <h2 className="text-2xl font-bold text-[#1B2A1B] mb-3 group-hover:text-[#3A7D44] transition-colors">
            {post.title}
          </h2>
          <p className="text-[#5A6B5A] leading-relaxed mb-4">{post.excerpt}</p>
          <div className="flex items-center gap-4 text-sm text-[#5A6B5A]">
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {post.readTime}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const BlogArticleView = ({ post, onBack }) => {
  return (
    <>
      <section className="pt-28 pb-8 bg-gradient-to-b from-[#F1F8F2] to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-[#3A7D44] font-medium mb-6 hover:gap-3 transition-all"
          >
            <ArrowRight size={16} className="rotate-180" />
            Back to Blog
          </button>
          <span className="inline-block px-3 py-1 bg-[#E8F5E9] text-[#3A7D44] text-xs font-semibold rounded-full mb-4">
            {post.category}
          </span>
          <h1
            className="text-3xl sm:text-4xl font-bold text-[#1B2A1B] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-[#5A6B5A] mb-8">
            <span>{post.date}</span>
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-64 sm:h-80 object-cover rounded-2xl mb-10"
          />
          <div className="prose prose-lg max-w-none">
            {post.content.split("\n\n").map((paragraph, index) => {
              const paragraphKey = `${post.id}-para-${index}`;
              if (paragraph.startsWith("**") && paragraph.endsWith("**")) {
                return (
                  <h3 key={paragraphKey} className="text-xl font-bold text-[#1B2A1B] mt-8 mb-3">
                    {paragraph.replace(/\*\*/g, "")}
                  </h3>
                );
              }
              if (paragraph.includes("**")) {
                const parts = paragraph.split(/\*\*(.*?)\*\*/);
                return (
                  <p key={paragraphKey} className="text-[#5A6B5A] leading-relaxed mb-4">
                    {parts.map((part, i) =>
                      i % 2 === 1 ? (
                        <strong key={`${paragraphKey}-b-${i}`} className="text-[#1B2A1B] font-semibold">{part}</strong>
                      ) : (
                        <span key={`${paragraphKey}-t-${i}`}>{part}</span>
                      )
                    )}
                  </p>
                );
              }
              if (paragraph.match(/^\d\./)) {
                const items = paragraph.split("\n");
                return (
                  <ol key={paragraphKey} className="list-decimal list-inside space-y-2 mb-4 text-[#5A6B5A]">
                    {items.map((item, i) => (
                      <li key={`${paragraphKey}-li-${i}`} className="leading-relaxed">{item.replace(/^\d\.\s*/, "")}</li>
                    ))}
                  </ol>
                );
              }
              return (
                <p key={paragraphKey} className="text-[#5A6B5A] leading-relaxed mb-4">{paragraph}</p>
              );
            })}
          </div>

          <div className="mt-12 p-8 bg-[#F1F8F2] rounded-2xl text-center">
            <h3 className="text-xl font-bold text-[#1B2A1B] mb-3">Have Questions About Your Coverage?</h3>
            <p className="text-[#5A6B5A] mb-6">
              I'm here to help you sort through your options — no pressure, no obligation.
            </p>
            <a
              href={siteConfig.calendlyUrl}
              
              
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-[#3A7D44] rounded-lg hover:bg-[#2D5A35] transition-colors"
            >
              Book a Free Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export { BlogCard, BlogFeaturedPost, BlogArticleView };
